import React from 'react';
import {
  BRAND_FULL_SRC,
  BRAND_MARK_SRC,
  BRAND_NAME,
  BRAND_TAGLINE,
} from '../../branding/assets';

export type BrandLogoVariant = 'full' | 'mark';
export type BrandLogoSurface = 'dark' | 'light' | 'bare';

/**
 * Platform-wide logo scale — sized for readable “GATI SUTRA” wordmark on desktop.
 *
 * - `sm`  — portal / compact chrome (~48px)
 * - `md`  — secondary headers / print (~56px)
 * - `lg`  — sidebar + primary app chrome (~64px)  ← default
 * - `xl`  — auth heroes / landing nav (~72px)
 */
export type BrandLogoSize = 'sm' | 'md' | 'lg' | 'xl';

const FULL_SIZE: Record<BrandLogoSize, { height: number; imgClass: string }> = {
  sm: { height: 48, imgClass: 'h-12 w-auto max-w-[210px] object-contain' },
  md: { height: 56, imgClass: 'h-14 w-auto max-w-[240px] object-contain' },
  lg: { height: 64, imgClass: 'h-16 w-auto max-w-[228px] object-contain' },
  xl: { height: 72, imgClass: 'h-[4.5rem] w-auto max-w-[320px] object-contain' },
};

const MARK_SIZE: Record<BrandLogoSize, { height: number; imgClass: string }> = {
  sm: { height: 44, imgClass: 'h-11 w-11 object-contain' },
  md: { height: 52, imgClass: 'h-[3.25rem] w-[3.25rem] object-contain' },
  lg: { height: 64, imgClass: 'h-16 w-16 object-contain' },
  xl: { height: 72, imgClass: 'h-[4.5rem] w-[4.5rem] object-contain' },
};

export type BrandLogoProps = {
  /** `full` = horizontal logo; `mark` = square icon mark */
  variant?: BrandLogoVariant;
  /**
   * Official assets use white/orange type on black.
   * - `dark`: logo sits on an already-dark UI (sidebar, hero)
   * - `light`: wraps logo in a dark brand plate for light pages
   * - `bare`: raw image only (emails / special layouts)
   */
  surface?: BrandLogoSurface;
  /** Preset size — prefer this over ad-hoc height / imgClassName */
  size?: BrandLogoSize;
  className?: string;
  /**
   * Extra classes merged onto the img (alignment only — do not override height/max-width).
   * e.g. `mx-auto` or `object-left`
   */
  imgClassName?: string;
  /** Accessible name; defaults to product name */
  alt?: string;
  /** @deprecated Prefer `size` — kept for rare one-offs */
  height?: number;
};

/**
 * Single source of truth for product branding imagery.
 * Prefer this over Lucide Truck / gradient placeholders.
 */
export const BrandLogo: React.FC<BrandLogoProps> = ({
  variant = 'full',
  surface = 'dark',
  size = 'lg',
  className,
  imgClassName,
  alt = `${BRAND_NAME} — ${BRAND_TAGLINE}`,
  height,
}) => {
  const src = variant === 'mark' ? BRAND_MARK_SRC : BRAND_FULL_SRC;
  const preset = variant === 'mark' ? MARK_SIZE[size] : FULL_SIZE[size];
  const resolvedHeight = height ?? preset.height;
  const style = { height: resolvedHeight, width: 'auto', maxHeight: resolvedHeight } as React.CSSProperties;

  const img = (
    <img
      src={src}
      alt={alt}
      draggable={false}
      decoding="async"
      style={style}
      className={[preset.imgClass, imgClassName].filter(Boolean).join(' ')}
    />
  );

  if (surface === 'bare') {
    return <span className={className ? `inline-flex items-center ${className}` : 'inline-flex items-center'}>{img}</span>;
  }

  if (surface === 'light') {
    const plateClass =
      variant === 'mark'
        ? 'inline-flex items-center justify-center rounded-2xl bg-[#0B0610] p-2.5 ring-1 ring-black/20'
        : 'inline-flex items-center rounded-2xl bg-[#0B0610] px-4 py-3 ring-1 ring-black/15';
    return <span className={className ? `${plateClass} ${className}` : plateClass}>{img}</span>;
  }

  return (
    <span className={className ? `inline-flex items-center ${className}` : 'inline-flex items-center'}>
      {img}
    </span>
  );
};

export default BrandLogo;
